import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCustomAction,
  useCreateCustomAction,
  useUpdateCustomAction,
} from '../hooks';
import type {
  CreateCustomActionRequest,
  UpdateCustomActionRequest,
  ToolFilterCondition,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { Breadcrumb, Tabs } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Select } from '@/shared/components/ui/Select';
import { Switch } from '@/shared/components/ui/Switch';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { SubItemRow } from '../components/form/SubItemRow';
import {
  SubItemModal,
  type SubItemKind,
  type HeaderDraft,
  type ParamDraft,
  type FilterDraft,
  type SubItemValue,
} from '../components/form/SubItemModal';
import {
  FIELD_OPTIONS,
  OPERATORS,
  OPERATORS_WITHOUT_VALUE,
  isJsonPathField,
} from '../components/filters/ToolFilterDefinitions';

type TabId = 'request' | 'ai' | 'filters';

const HTTP_METHODS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const MAX_FILTERS = 10;
const BODY_METHODS = ['POST', 'PUT', 'PATCH'];

// ---------------------------------------------------------------------------
// Helpers de exibição
// ---------------------------------------------------------------------------
const capitalize = (t: string): string => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

const describeParam = (p: ParamDraft): string =>
  `${capitalize(p.type)} · ${p.description || 'Sem descrição'}${p.required ? ' · Obrigatório' : ''}`;

const describeFilterField = (field: string): string => {
  if (isJsonPathField(field)) return field || '$.';
  const opt = FIELD_OPTIONS.find((o) => o.value === field);
  return opt ? `${opt.category} · ${opt.label}` : field || 'Campo';
};

const describeFilter = (f: ToolFilterCondition): string => {
  const op = OPERATORS.find((o) => o.value === f.operator)?.label ?? f.operator;
  return OPERATORS_WITHOUT_VALUE.includes(f.operator) ? op : `${op} · "${f.value}"`;
};

// ---------------------------------------------------------------------------
// Sub-componentes presentacionais (module scope — evita remount)
// ---------------------------------------------------------------------------
const AddButton: React.FC<{
  label: string;
  onClick: () => void;
  testId: string;
  disabled?: boolean;
}> = ({ label, onClick, testId, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="w-full flex items-center justify-center gap-2 rounded-lg border py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
    onMouseEnter={(e) => {
      if (!e.currentTarget.disabled) {
        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
  >
    <Icon name="plus" className="w-4 h-4" />
    {label}
  </button>
);

const EmptyLine: React.FC<{ children: React.ReactNode; centered?: boolean }> = ({
  children,
  centered = false,
}) => (
  <p
    className={`text-sm ${centered ? 'text-center py-5' : 'py-2'}`}
    style={{ color: 'var(--theme-muted-text)' }}
  >
    {children}
  </p>
);

export const CustomActionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: existingAction, isLoading: loadingAction } = useCustomAction(id || '');
  const createMutation = useCreateCustomAction();
  const updateMutation = useUpdateCustomAction();

  // Form state
  const [formData, setFormData] = useState<CreateCustomActionRequest>({
    name: '',
    displayName: '',
    description: '',
    parameters: { type: 'object', properties: {}, required: [] },
    endpoint: { method: 'GET', url: '' },
    credentials: undefined,
    responseMapping: { successPath: '$.data', errorPath: '$.error.message' },
  });

  const [parameters, setParameters] = useState<ParamDraft[]>([]);
  const [headers, setHeaders] = useState<HeaderDraft[]>([]);
  const [bodyText, setBodyText] = useState('');

  const [activeTab, setActiveTab] = useState<TabId>('request');
  const [filters, setFilters] = useState<ToolFilterCondition[]>([]);
  const [aiProcessingEnabled, setAiProcessingEnabled] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiParameters, setAiParameters] = useState<ParamDraft[]>([]);

  // Sub-item modal state
  const [modal, setModal] = useState<{ kind: SubItemKind; index: number | null } | null>(null);

  // Load existing action data
  useEffect(() => {
    if (existingAction) {
      setFormData({
        name: existingAction.name,
        displayName: existingAction.displayName,
        description: existingAction.description || '',
        parameters: existingAction.parameters,
        endpoint: {
          method: existingAction.endpoint.method,
          url: existingAction.endpoint.url,
        },
        credentials: existingAction.credentials,
        responseMapping: existingAction.responseMapping,
      });

      // Convert parameters schema to editable list
      const paramsList: ParamDraft[] = Object.entries(existingAction.parameters.properties || {}).map(
        ([name, schema]: [string, { type: string; description: string }]) => ({
          name,
          type: schema.type || 'string',
          description: schema.description || '',
          required: existingAction.parameters.required?.includes(name) || false,
        })
      );
      setParameters(paramsList);

      // Convert headers to editable list
      const headersList: HeaderDraft[] = Object.entries(existingAction.endpoint.headers || {}).map(
        ([key, value]) => ({ key, value: String(value) })
      );
      setHeaders(headersList);

      // Body template as text
      setBodyText(
        existingAction.endpoint.body ? JSON.stringify(existingAction.endpoint.body, null, 2) : ''
      );

      // Filters
      if (existingAction.filters) {
        setFilters(existingAction.filters);
      }

      // AI processing config
      if (existingAction.aiProcessing) {
        setAiProcessingEnabled(existingAction.aiProcessing.enabled);
        setAiPrompt(existingAction.aiProcessing.prompt || '');
        const aiParamsList: ParamDraft[] = Object.entries(
          existingAction.aiProcessing.parameters?.properties || {}
        ).map(([name, schema]: [string, { type: string; description: string }]) => ({
          name,
          type: schema.type || 'string',
          description: schema.description || '',
          required: existingAction.aiProcessing?.parameters?.required?.includes(name) || false,
        }));
        setAiParameters(aiParamsList);
      }
    }
  }, [existingAction]);

  const canSave = !!(formData.name && formData.displayName && formData.endpoint.url);
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const pageTitle = isEditMode
    ? existingAction?.displayName || 'Editar Ferramenta'
    : 'Nova Ferramenta';
  const hasBody = BODY_METHODS.includes(formData.endpoint.method);
  const aiPromptError =
    aiPrompt.length > 0 && aiPrompt.length < 10
      ? 'O prompt deve ter pelo menos 10 caracteres.'
      : undefined;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;

    // Parameters → JSON Schema
    const parametersProps: Record<string, { type: string; description: string }> = {};
    const parametersRequired: string[] = [];
    parameters.forEach((param) => {
      if (!param.name) return;
      parametersProps[param.name] = { type: param.type, description: param.description };
      if (param.required) parametersRequired.push(param.name);
    });
    const parametersSchema = {
      type: 'object' as const,
      properties: parametersProps,
      required: parametersRequired,
    };

    // Headers → object
    const headersObj: Record<string, string> = {};
    headers.forEach((header) => {
      if (header.key && header.value) headersObj[header.key] = header.value;
    });

    // AI processing (always send so disabling persists)
    const aiParamsProps: Record<string, { type: string; description: string }> = {};
    const aiParamsRequired: string[] = [];
    if (aiProcessingEnabled) {
      aiParameters.forEach((param) => {
        if (!param.name) return;
        aiParamsProps[param.name] = { type: param.type, description: param.description };
        if (param.required) aiParamsRequired.push(param.name);
      });
    }
    const aiProcessing = {
      enabled: aiProcessingEnabled,
      prompt: aiProcessingEnabled ? aiPrompt : '',
      parameters: {
        type: 'object' as const,
        properties: aiParamsProps,
        required: aiParamsRequired,
      },
    };

    // Body template string → object
    let bodyObj: Record<string, unknown> | undefined;
    const trimmedBody = bodyText.trim();
    if (trimmedBody) {
      try {
        bodyObj = JSON.parse(trimmedBody) as Record<string, unknown>;
      } catch {
        bodyObj = undefined;
      }
    }

    const payload: CreateCustomActionRequest = {
      ...formData,
      parameters: parametersSchema,
      endpoint: {
        ...formData.endpoint,
        headers: headersObj,
        body: bodyObj,
      },
      aiProcessing,
      filters: filters.length > 0 ? filters : undefined,
    };

    try {
      if (isEditMode && id) {
        await updateMutation.mutateAsync({ id, data: payload as UpdateCustomActionRequest });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate('/automation/custom-actions');
    } catch (error) {
      console.error('Error saving custom action:', error);
    }
  };

  // ---- Sub-item modal helpers ----------------------------------------------
  const modalInitial = (): SubItemValue | null => {
    if (!modal || modal.index === null) return null;
    const { kind, index } = modal;
    if (kind === 'header') return headers[index] ?? null;
    if (kind === 'param') return parameters[index] ?? null;
    if (kind === 'aiparam') return aiParameters[index] ?? null;
    return filters[index] ?? null;
  };

  const handleModalSave = (value: SubItemValue) => {
    if (!modal) return;
    const { kind, index } = modal;
    if (kind === 'header') {
      const v = value as HeaderDraft;
      setHeaders((prev) => (index === null ? [...prev, v] : prev.map((x, i) => (i === index ? v : x))));
    } else if (kind === 'param') {
      const v = value as ParamDraft;
      setParameters((prev) => (index === null ? [...prev, v] : prev.map((x, i) => (i === index ? v : x))));
    } else if (kind === 'aiparam') {
      const v = value as ParamDraft;
      setAiParameters((prev) => (index === null ? [...prev, v] : prev.map((x, i) => (i === index ? v : x))));
    } else {
      const v = value as FilterDraft;
      setFilters((prev) => (index === null ? [...prev, v] : prev.map((x, i) => (i === index ? v : x))));
    }
    setModal(null);
  };

  const removeAt = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>, index: number): void => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // Loading state (edit mode)
  if (isEditMode && loadingAction) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <Icon
          name="loader"
          className="w-8 h-8 animate-spin"
          style={{ color: 'var(--theme-button-primary)' }}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header (borda inferior vem da barra de abas — Tabs pageLevel, F55) */}
      <div
        className="flex-shrink-0"
        style={{ backgroundColor: 'var(--theme-card-background)' }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Breadcrumb shared (F25-31 — nunca seta-voltar) */}
            <Breadcrumb
              items={[
                {
                  label: 'Automação',
                  onNavigate: () => navigate('/automation/custom-actions'),
                  testid: 'tool-form-breadcrumb-automation',
                },
                {
                  label: 'Ferramentas',
                  onNavigate: () => navigate('/automation/custom-actions'),
                  testid: 'tool-form-breadcrumb-tools',
                },
                { label: pageTitle, testid: 'tool-form-breadcrumb-current' },
              ]}
              mobileLegend="Automação · Ferramentas"
              data-testid="tool-form-breadcrumb"
            />

            {/* Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {!canSave && (
                <span
                  className="hidden md:inline text-sm"
                  style={{ color: 'var(--theme-warning-color)' }}
                >
                  Preencha os campos obrigatórios
                </span>
              )}
              <Button
                variant="secondary"
                className="hidden sm:inline-flex"
                onClick={() => navigate('/automation/custom-actions')}
                data-testid="tool-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                icon="check"
                loading={isSaving}
                onClick={() => handleSubmit()}
                disabled={!canSave || isSaving}
                data-testid="tool-form-save"
              >
                {isSaving ? (
                  isEditMode ? 'Salvando...' : 'Criando...'
                ) : (
                  <>
                    <span className="sm:hidden">{isEditMode ? 'Salvar' : 'Criar'}</span>
                    <span className="hidden sm:inline">{isEditMode ? 'Salvar Alterações' : 'Criar Ferramenta'}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Abas coladas ao header — Tabs shared pageLevel (F55) */}
        <Tabs
          pageLevel
          variant="underline"
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as TabId)}
          tabs={[
            {
              id: 'request',
              icon: 'settings',
              label: <span data-testid="tool-form-tab-request">Requisição</span>,
              content: null,
            },
            {
              id: 'ai',
              icon: 'cpu',
              label: (
                <span className="inline-flex items-center gap-1.5" data-testid="tool-form-tab-ai">
                  Processamento com IA
                  {aiProcessingEnabled && (
                    <span
                      className="px-1.5 py-0.5 text-xs rounded-full"
                      style={{ backgroundColor: 'var(--theme-success-subtle)', color: 'var(--theme-success-fg)' }}
                    >
                      Ativo
                    </span>
                  )}
                </span>
              ),
              content: null,
            },
            {
              id: 'filters',
              icon: 'filter',
              label: <span data-testid="tool-form-tab-filters">Filtros</span>,
              badge: filters.length,
              content: null,
            },
          ]}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ==================== TAB: REQUEST ==================== */}
          {activeTab === 'request' && (
            <>
              {/* Informações Básicas */}
              <FormSection
                icon="tool"
                title="Informações Básicas"
                description="Identificação da ferramenta para o agente de IA"
              >
                <div className="space-y-4">
                  <FormField
                    label="Nome Técnico"
                    required
                    description="Nome usado pelo AI Agent para invocar a ferramenta (snake_case)"
                  >
                    <Input
                      className="font-mono"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: get_customer_data"
                      data-testid="tool-form-name"
                    />
                  </FormField>

                  <FormField label="Nome de Exibição" required>
                    <Input
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      placeholder="Ex: Buscar Dados do Cliente"
                      data-testid="tool-form-display-name"
                    />
                  </FormField>

                  <FormField label="Descrição">
                    <Textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descreva quando o agente deve usar esta ferramenta"
                      rows={3}
                      data-testid="tool-form-description"
                    />
                  </FormField>
                </div>
              </FormSection>

              {/* Configuração do Endpoint */}
              <FormSection
                icon="link"
                title="Configuração do Endpoint"
                description="Requisição HTTP executada quando o agente chama a ferramenta"
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
                    <div className="md:col-span-1">
                      <FormField label="Método HTTP" required>
                        <Select
                          options={HTTP_METHODS}
                          value={formData.endpoint.method}
                          onChange={(v) =>
                            setFormData({
                              ...formData,
                              endpoint: {
                                ...formData.endpoint,
                                method: v as CreateCustomActionRequest['endpoint']['method'],
                              },
                            })
                          }
                          data-testid="tool-form-method"
                        />
                      </FormField>
                    </div>
                    <div className="md:col-span-1">
                      <FormField label="URL" required>
                        <Input
                          type="url"
                          className="font-mono"
                          value={formData.endpoint.url}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              endpoint: { ...formData.endpoint, url: e.target.value },
                            })
                          }
                          placeholder="https://api.example.com/endpoint"
                          data-testid="tool-form-url"
                        />
                      </FormField>
                    </div>
                  </div>

                  {/* Headers */}
                  <FormField label="Headers">
                    <div className="space-y-2">
                      {headers.map((header, index) => (
                        <SubItemRow
                          key={index}
                          icon="link"
                          mono
                          monoSub
                          title={header.key || 'Sem nome'}
                          subtitle={header.value || '—'}
                          onEdit={() => setModal({ kind: 'header', index })}
                          onRemove={() => removeAt(setHeaders, index)}
                          testId={`tool-header-${index}`}
                        />
                      ))}
                      {headers.length === 0 && <EmptyLine>Nenhum header configurado</EmptyLine>}
                      <AddButton
                        label="Adicionar Header"
                        onClick={() => setModal({ kind: 'header', index: null })}
                        testId="tool-add-header"
                      />
                    </div>
                  </FormField>

                  {/* Body Template */}
                  {hasBody && (
                    <FormField
                      label="Body Template"
                      description="Use {parameterName} para interpolar valores. Só para POST/PUT/PATCH."
                    >
                      <Textarea
                        className="font-mono"
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val) {
                            try {
                              setBodyText(JSON.stringify(JSON.parse(val), null, 2));
                            } catch {
                              // mantém como está se não for JSON válido
                            }
                          }
                        }}
                        placeholder={'{"key": "{parameterName}"}'}
                        rows={4}
                        data-testid="tool-form-body"
                      />
                    </FormField>
                  )}
                </div>
              </FormSection>

              {/* Parâmetros */}
              <FormSection
                icon="file-text"
                title="Parâmetros"
                description="Entradas que o agente preenche ao chamar a ferramenta (viram JSON Schema)"
              >
                <div className="space-y-2">
                  {parameters.map((param, index) => (
                    <SubItemRow
                      key={index}
                      icon="file-text"
                      mono
                      title={param.name || 'Sem nome'}
                      subtitle={describeParam(param)}
                      onEdit={() => setModal({ kind: 'param', index })}
                      onRemove={() => removeAt(setParameters, index)}
                      testId={`tool-param-${index}`}
                    />
                  ))}
                  {parameters.length === 0 && (
                    <EmptyLine centered>
                      Nenhum parâmetro configurado. Clique em &quot;Adicionar Parâmetro&quot; para começar.
                    </EmptyLine>
                  )}
                  <AddButton
                    label="Adicionar Parâmetro"
                    onClick={() => setModal({ kind: 'param', index: null })}
                    testId="tool-add-param"
                  />
                </div>
              </FormSection>

              {/* Mapeamento de Resposta */}
              <FormSection
                icon="check"
                title="Mapeamento de Resposta"
                description="Onde ler o resultado e o erro na resposta HTTP (JSONPath)"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="JSONPath para Sucesso">
                    <Input
                      className="font-mono"
                      value={formData.responseMapping?.successPath || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          responseMapping: {
                            ...formData.responseMapping,
                            successPath: e.target.value,
                          },
                        })
                      }
                      placeholder="$.data"
                      data-testid="tool-form-success-path"
                    />
                  </FormField>
                  <FormField label="JSONPath para Erro">
                    <Input
                      className="font-mono"
                      value={formData.responseMapping?.errorPath || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          responseMapping: {
                            ...formData.responseMapping,
                            errorPath: e.target.value,
                          },
                        })
                      }
                      placeholder="$.error.message"
                      data-testid="tool-form-error-path"
                    />
                  </FormField>
                </div>
              </FormSection>
            </>
          )}

          {/* ==================== TAB: AI PROCESSING ==================== */}
          {activeTab === 'ai' && (
            <FormSection
              icon="cpu"
              title="Processamento com IA"
              description="Quando habilitado, a resposta HTTP será processada por um modelo de IA antes de ser retornada ao agente."
            >
              <div className="space-y-5">
                <div
                  className="flex items-center justify-between gap-4"
                  data-testid="tool-form-ai-toggle"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      Habilitar processamento
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                      A resposta crua da API passa pelo modelo antes de chegar ao agente
                    </p>
                  </div>
                  <Switch checked={aiProcessingEnabled} onChange={setAiProcessingEnabled} />
                </div>

                {aiProcessingEnabled && (
                  <>
                    <FormField
                      label="Prompt de IA"
                      required
                      description="Mínimo de 10 caracteres. Use {{nomeParametro}} para interpolar os parâmetros da chamada."
                      error={aiPromptError}
                    >
                      <Textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Instrua como transformar a resposta. Use {{nomeParametro}} para interpolar parâmetros."
                        rows={6}
                        data-testid="tool-form-ai-prompt"
                      />
                    </FormField>

                    <FormField label="Parâmetros de IA">
                      <div className="space-y-2">
                        {aiParameters.map((param, index) => (
                          <SubItemRow
                            key={index}
                            icon="cpu"
                            mono
                            title={param.name || 'Sem nome'}
                            subtitle={describeParam(param)}
                            onEdit={() => setModal({ kind: 'aiparam', index })}
                            onRemove={() => removeAt(setAiParameters, index)}
                            testId={`tool-aiparam-${index}`}
                          />
                        ))}
                        {aiParameters.length === 0 && (
                          <EmptyLine>
                            Nenhum parâmetro de IA configurado. O prompt receberá apenas a resposta HTTP.
                          </EmptyLine>
                        )}
                        <AddButton
                          label="Adicionar Parâmetro"
                          onClick={() => setModal({ kind: 'aiparam', index: null })}
                          testId="tool-add-aiparam"
                        />
                      </div>
                    </FormField>
                  </>
                )}
              </div>
            </FormSection>
          )}

          {/* ==================== TAB: FILTERS ==================== */}
          {activeTab === 'filters' && (
            <FormSection
              icon="filter"
              title="Filtros de Ativação"
              description="Defina condições para que esta ferramenta seja disponibilizada ao agente de IA. Todos os filtros devem ser atendidos (lógica E)."
            >
              <div className="space-y-2">
                <div className="flex justify-end">
                  <span className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                    {filters.length} / {MAX_FILTERS} filtros
                  </span>
                </div>
                {filters.map((filter, index) => (
                  <SubItemRow
                    key={index}
                    icon="filter"
                    title={describeFilterField(filter.field)}
                    subtitle={describeFilter(filter)}
                    onEdit={() => setModal({ kind: 'filter', index })}
                    onRemove={() => removeAt(setFilters, index)}
                    testId={`tool-filter-${index}`}
                  />
                ))}
                {filters.length === 0 && (
                  <div className="text-center py-5">
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      Nenhum filtro configurado
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                      Sem filtros, esta ferramenta estará sempre disponível para o agente de IA.
                    </p>
                  </div>
                )}
                <AddButton
                  label="Adicionar Filtro"
                  onClick={() => setModal({ kind: 'filter', index: null })}
                  testId="tool-add-filter"
                  disabled={filters.length >= MAX_FILTERS}
                />
              </div>
            </FormSection>
          )}
        </form>
      </div>
      </div>

      {/* Sub-item modal (tema claro fixo) */}
      {modal && (
        <SubItemModal
          key={`${modal.kind}-${modal.index ?? 'new'}`}
          kind={modal.kind}
          initial={modalInitial()}
          onClose={() => setModal(null)}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

CustomActionFormPage.displayName = 'CustomActionFormPage';
